import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, ShoppingBag, Heart, MapPin, LogOut } from "lucide-react";
import { T } from "../../constants/theme";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { CartContext } from "../../contexts/CartContext";
import { WishlistContext } from "../../contexts/WishlistContext";
import { apiFetch, readApiBody } from "../../services/api";
import { useAuthedApi } from "../../hooks/useAuthedApi";
import OrdersManager from "../../components/account/OrdersManager";

export default function ProfilePage() {
  const navigate = useNavigate();

  function AccountSkeleton(){

return(

<div style={{ padding:50 }}>

<div style={{ height:300, background:"#eee", borderRadius:25, animation:"pulse 1.5s infinite" }} /> </div> )

}

const {
  user,
  setUser,
  token,
  logout
}=useContext(AuthContext);


const toast = useContext(ToastContext);
const { addItem } = useContext(CartContext);
const { wishlist, loading: wishlistLoading, removeFromWishlist, refreshWishlist } = useContext(WishlistContext);


const [activeTab,setActiveTab]=useState("profile");

const [showEditProfile,setShowEditProfile]=useState(false);

const [savingProfile,setSavingProfile]=useState(false);


const [profileImage,setProfileImage]=useState(null);


const [avatarUrl,setAvatarUrl]=useState("");

const [profilePreview,setProfilePreview]=useState("");


const [profileForm,setProfileForm]=useState({

first_name:user?.first_name || "",

last_name:user?.last_name || "",

email:user?.email || "",

});


useEffect(()=>{

if(user){

setProfileForm({

first_name:user.first_name || "",

last_name:user.last_name || "",

email:user.email || "",

});

}

},[user]);

useEffect(()=>{

if(!user?.id){
setAvatarUrl("");
return;
}

let isMounted=true;

(async()=>{
try{
const res=await apiFetch(`/images/profile/${user.id}`);
if(!res.ok){
return;
}
const data=await res.json();
if(isMounted && data?.url){
setAvatarUrl(data.url);
}
}catch{
}
})();

return()=>{isMounted=false;};

},[user?.id]);

useEffect(()=>{

if(!profileImage) setProfilePreview(avatarUrl);

},[avatarUrl,profileImage]);

const [showAddressModal, setShowAddressModal] = useState(false);

const emptyAddressForm = {
    label: "",
    first_name: "",
    last_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state_province: "",
    postalcode: "",
    country: "United Kingdom",
    is_default: false,
};

const [addressForm, setAddressForm] = useState(emptyAddressForm);
const [editingAddress, setEditingAddress] = useState(null);
const [savingAddress, setSavingAddress] = useState(false);

// API DATA

const {
data:orders=[],
loading:ordersLoading
}=useAuthedApi(
"/orders",
token,
[token]
);


const [addresses, setAddresses] = useState([]);
const [loadingAddresses, setLoadingAddresses] = useState(false);

const loadAddresses = useCallback(async () => {
    if (!token) {
        setAddresses([]);
        return;
    }

    setLoadingAddresses(true);

    try {
        const res = await apiFetch(
            "/addresses",
            {},
            { authToken: token }
        );

        const body = await res.json();

        console.log("Addresses:", body);

        setAddresses(body);

    } catch (e) {
        console.error(e);
    } finally {
        setLoadingAddresses(false);
    }
}, [token]);

useEffect(() => {
    loadAddresses();
}, [loadAddresses]);

// Reload wishlist data whenever the Account page is visited, so a stale
// or empty context (e.g. right after a session was restored) shows the
// user's current wishlist instead of waiting on an unrelated token change.
useEffect(() => {
    if (!token) return;
    refreshWishlist();
}, [token, refreshWishlist]);

function openAddAddress() {
    setEditingAddress(null);
    setAddressForm(emptyAddressForm);
    setShowAddressModal(true);
}

function openEditAddress(address) {
    setEditingAddress(address);
    setAddressForm({
        label: address.label || "",
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        address_line1: address.address_line1 || "",
        address_line2: address.address_line2 || "",
        country: address.country || "",
        state_province: address.state_province || "",
        city: address.city || "",
        postalcode: address.postalcode || "",
        is_default: Boolean(address.is_default || address.default),
    });
    setShowAddressModal(true);
}

function closeAddressModal() {
    setShowAddressModal(false);
    setEditingAddress(null);
    setAddressForm(emptyAddressForm);
}

async function submitAddress() {
    setSavingAddress(true);
    try {
        const payload = { ...addressForm };
        const isEdit = Boolean(editingAddress);
        const res = await apiFetch(
            isEdit ? `/addresses/${editingAddress.id}` : "/addresses",
            {
                method: isEdit ? "PATCH" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            },
            { authToken: token }
        );
        const body = await readApiBody(res);
        if (!res.ok) {
            throw new Error(body?.error || body?.message || "Failed to save address");
        }
        toast?.(isEdit ? "Address updated" : "Address added", "success");
        closeAddressModal();
        await loadAddresses();
    } catch (e) {
        toast?.(e.message || "Failed to save address", "error");
    } finally {
        setSavingAddress(false);
    }
}

async function deleteAddress(address) {
    if (!window.confirm("Delete this address?")) return;
    try {
        const res = await apiFetch(
            `/addresses/${address.id}`,
            { method: "DELETE" },
            { authToken: token }
        );
        if (!res.ok) {
            const body = await readApiBody(res);
            throw new Error(body?.error || body?.message || "Failed to delete address");
        }
        toast?.("Address deleted", "success");
        await loadAddresses();
    } catch (e) {
        toast?.(e.message || "Failed to delete address", "error");
    }
}

useEffect(()=>{


const style=document.createElement("style");


style.innerHTML=`

.account-layout{

width:100%;

}



@media(max-width:900px){


.account-layout{

grid-template-columns:1fr !important;

}



.account-sidebar{


position:relative!important;


}



.profile-grid{

grid-template-columns:1fr!important;

}



.account-header h1{

font-size:32px!important;

}



}



@media(max-width:600px){


.account-page{

padding:30px 15px!important;

}


}
.address-input{
    width:100%;
    padding:14px 16px;
    border:1px solid rgba(0,0,0,0.06);
    border-radius:14px;
    outline:none;
    font-size:15px;
    transition:all .25s ease;
    box-sizing:border-box;
}

.address-input:hover{
    border-color:#111;
}

.address-input:focus{
    border:2px solid #111;
    box-shadow:none;
}



`;


document.head.appendChild(style);


return()=>{

document.head.removeChild(style);

}


},[]);

useEffect(()=>{
  const s=document.createElement('style');
  s.innerHTML=`
  /* Premium profile CTA styles */
  .profile-cta{
    height:52px !important;
    padding:0 28px !important;
    background: ${T.gold} !important;
    color: ${T.white} !important;
    border: none !important;
    border-radius: 0 !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: .12em !important;
    font-size: 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    transition: transform .22s ease, box-shadow .22s ease, background .22s ease !important;
    box-shadow: 0 8px 24px rgba(0,0,0,.08) !important;
  }
  .profile-cta:hover{ background: #111 !important; color: #fff !important; transform: translateY(-2px) !important; box-shadow: 0 18px 40px rgba(0,0,0,.18) !important; }
  .profile-cta.secondary{ background: #fff !important; color: #111 !important; border: 1px solid ${T.border} !important; }
  .profile-cta.secondary:hover{ background: #111 !important; color: #fff !important; border-color: #111 !important; transform: translateY(-2px) !important; box-shadow: 0 18px 40px rgba(0,0,0,.18) !important; }
  .profile-cta-icon{
    width:52px !important;
    height:52px !important;
    border-radius:0 !important;
    border:1px solid ${T.border} !important;
    background:#fff !important;
    display:inline-flex !important;
    align-items:center !important;
    justify-content:center !important;
    cursor:pointer !important;
    transition:transform .22s ease, box-shadow .22s ease, background .22s ease, color .22s ease, border-color .22s ease !important;
    box-shadow: 0 8px 24px rgba(0,0,0,.08) !important;
  }
  .profile-cta-icon:hover{ background:#111 !important; border-color:#111 !important; color:#fff !important; transform: translateY(-2px) !important; box-shadow: 0 18px 40px rgba(0,0,0,.18) !important; }
  `;
  document.head.appendChild(s);
  return ()=>document.head.removeChild(s);
},[]);

if(!user){

return (

<AccountSkeleton/>

)

}

 const cardStyle={

background:T.white,


padding:28,

border:`1px solid ${T.border}`,

boxShadow:
"0 20px 50px rgba(0,0,0,.05)",

};



const menuItems=[


{
id:"profile",
label:"Profile Details",
icon:User
},


{
id:"orders",
label:"Order History",
icon:ShoppingBag
},


{
id:"wishlist",
label:"Wishlist",
icon:Heart
},


{
id:"addresses",
label:"Saved Addresses",
icon:MapPin
},




];



const menuButton=(active)=>({


display:"flex",

alignItems:"center",

gap:12,

width:"100%",


padding:"14px 16px",


border:"none",





cursor:"pointer",


fontSize:14,


fontWeight:600,


background:

active

?T.goldLight

:"#fff",


color:

active

?T.gold

:T.ink,


transition:"all .25s ease"



});
 function handleProfileImage(e){


const file=e.target.files[0];


if(!file) return;



setProfileImage(file);



setProfilePreview(
URL.createObjectURL(file)
);


}
async function saveProfile() {
  try {
    setSavingProfile(true);

    const formData = new FormData();

    formData.append(
      "first_name",
      profileForm.first_name
    );

    formData.append(
      "last_name",
      profileForm.last_name || ""
    );

    formData.append(
      "email",
      profileForm.email
    );

    if (profileImage) {
      formData.append(
        "image",
        profileImage
      );
    }

    const res = await apiFetch(
      "/auth/profile",
      {
        method: "PUT",
        body: formData,
      },
      { authToken: token }
    );

    const data = await readApiBody(res);

    if (!res.ok) {
      throw new Error(
        data.error ||
        data.message ||
        "Profile update failed"
      );
    }

    setUser(data);

    setProfileImage(null);

    const profileUserId = data.id || user?.id;
    if (profileUserId) {
      const imageRes = await apiFetch(`/images/profile/${profileUserId}`);
      if (imageRes.ok) {
        const imageData = await imageRes.json();
        if (imageData?.url) {
          setAvatarUrl(imageData.url);
          setProfilePreview(imageData.url);
        }
      }
    }

    toast(
      "Profile updated successfully",
      "success"
    );

    setShowEditProfile(false);

  } catch (err) {
    toast(
      err.message || "Profile update failed",
      "error"
    );
  } finally {
    setSavingProfile(false);
  }
}
  // ─── LOADING USER ──────────────────────────────────────────────────


 return(

<div

className="account-page"

style={{

background:T.paper,

minHeight:"100vh",

padding:"50px 24px 90px"

}}

>


<div

style={{

maxWidth:1250,

margin:"auto"

}}

>


{/* HEADER */}

<motion.div

initial={{opacity:0,y:-20}}

animate={{opacity:1,y:0}}

transition={{duration:.4}}

style={{

marginBottom:40

}}

>
<div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:20}}>
  <div>
    <div
      style={{
        textTransform: "uppercase",
        letterSpacing: ".2em",
        fontSize: 12,
        fontWeight: 700,
        marginBottom: 12,
        color:T.gold,
      }}
    >
      Your Account 
    </div>

    <div
      style={{
        margin: 0,
        fontFamily: "Playfair Display, serif",
        fontSize:35,
        fontWeight: 600,
        color:T.ink,
        lineHeight: 1.05,
      }}
    >
      Welcome back, {user.first_name}
    </div>
  </div>

  <button
    style={{
      display:"flex",
      alignItems:"center",
      gap:12,
      padding:"14px 18px",
      border:"none",
      borderRadius:14,
      cursor:"pointer",
      fontSize:14,
      fontWeight:600,
      background:"#fff",
      color:"#c0392b",
      transition:"all .25s ease",
      whiteSpace:"nowrap"
    }}
    onClick={logout}
  >
    <LogOut size={18}/>
    Logout
  </button>
</div>

</motion.div>





<div

className="account-layout"

style={{

display:"grid",

gridTemplateColumns:"300px minmax(0,1fr)",

gap:30,

alignItems:"start"

}}

>


{/* SIDEBAR */}


<motion.aside

className="account-sidebar"

initial={{opacity:0,x:-30}}

animate={{opacity:1,x:0}}

style={cardStyle}

>



<div

style={{

display:"flex",

justifyContent:"center"

}}

>

<div

style={{

width:120,

height:120,

borderRadius:"50%",

background:T.goldLight,

display:"flex",

alignItems:"center",

justifyContent:"center",

fontSize:38,

fontWeight:800,

color:T.gold,

position:"relative",

overflow:"hidden"

}}

>


{avatarUrl ?


<img

src={avatarUrl}

alt="profile"

style={{

width:"100%",

height:"100%",

objectFit:"cover"

}}

/>


:


user.first_name?.charAt(0)

}






</div>

</div>



<h3

style={{

textAlign:"center",

marginTop:20

}}

>

{user.first_name}

{" "}

{user.last_name}

</h3>



<p

style={{

textAlign:"center",

color:T.muted

}}

>

{user.email}

</p>



<hr

style={{

margin:"25px 0",

borderColor:T.border

}}

/>



<div

style={{

display:"grid",

gap:10

}}

>


{menuItems.map(item=>{


const Icon=item.icon;


return(

<motion.button


whileHover={{x:5}}


key={item.id}


style={menuButton(
activeTab===item.id
)}


onClick={()=>setActiveTab(item.id)}


>


<Icon size={18}/>


{item.label}


</motion.button>


)

})}



</div>



</motion.aside>





{/* CONTENT */}

<motion.main

initial={{opacity:0}}

animate={{opacity:1}}

style={{

minHeight:500

}}

>


<AnimatePresence mode="wait">


<motion.div

key={activeTab}

initial={{

opacity:0,

x:30

}}

animate={{

opacity:1,

x:0

}}

exit={{

opacity:0,

x:-30

}}

transition={{

duration:.3

}}

>


{activeTab==="profile" &&

<ProfileOverview

user={user}

setShowEditProfile={setShowEditProfile}

/>

}
{activeTab === "orders" && (
  <OrdersManager
    orders={orders}
    loading={ordersLoading}
    navigate={navigate}
    addItem={addItem}
    toast={toast}
  />
)}

{activeTab === "wishlist" && (
    <WishlistTab
        wishlist={wishlist}
        loading={wishlistLoading}
        onAddToCart={(item) => {
            addItem(item.product_id);
            toast?.("Added to basket 👕", "success");
        }}
        onRemove={(item) => removeFromWishlist(item.product_id)}
    />
)}

{activeTab === "addresses" && (
    <AddressesTab
        addresses={addresses}
        loading={loadingAddresses}
        setShowAddressModal={setShowAddressModal}
        onAddAddress={openAddAddress}
        onEditAddress={openEditAddress}
        onDeleteAddress={deleteAddress}
    />
)}



</motion.div>


</AnimatePresence>


</motion.main>


</div>


</div>
{showEditProfile && (

  <EditProfileModal

    user={user}

    profileForm={profileForm}

    setProfileForm={setProfileForm}

    profilePreview={profilePreview}

    handleProfileImage={handleProfileImage}

    setShowEditProfile={setShowEditProfile}

    saveProfile={saveProfile}

    savingProfile={savingProfile}

  />

)}

{showAddressModal && (
    <AddressModal
        addressForm={addressForm}
        setAddressForm={setAddressForm}
        setShowAddressModal={closeAddressModal}
        editingAddress={editingAddress}
        savingAddress={savingAddress}
        onSubmit={submitAddress}
    />
)}

</div>

);

}
function EditProfileModal({

user,

profileForm,

setProfileForm,

profilePreview,

handleProfileImage,

setShowEditProfile,

saveProfile,

savingProfile

}){


return(


<motion.div


initial={{
opacity:0
}}


animate={{
opacity:1
}}


exit={{
opacity:0
}}



style={{

position:"fixed",

inset:0,

background:"rgba(0,0,0,.45)",

backdropFilter:"blur(8px)",

display:"flex",

alignItems:"center",

justifyContent:"center",

zIndex:9999,

padding:20

}}



>


<motion.div


initial={{

scale:.9,

y:30

}}


animate={{

scale:1,

y:0

}}



style={{

width:"100%",

maxWidth:520,

background:"#fff",


padding:35,

boxShadow:
"0 12px 32px rgba(0,0,0,0.05)"

}}


>


<h2

style={{

marginTop:0

}}

>

Edit Profile

</h2>



<p

style={{

color:T.muted,

fontSize:14

}}

>

Update your personal information

</p>





{/* PROFILE IMAGE */}


<div

style={{

display:"flex",

justifyContent:"center",

margin:"25px 0"

}}

>


<label

style={{

cursor:"pointer"

}}

>


<div

style={{

width:110,

height:110,

borderRadius:"50%",

overflow:"hidden",

background:T.goldLight,

display:"flex",

alignItems:"center",

justifyContent:"center",

fontSize:35,

fontWeight:700,

color:T.gold

}}

>



{profilePreview ?


<img

src={profilePreview}

style={{

width:"100%",

height:"100%",

objectFit:"cover"

}}

/>


:

user.first_name?.charAt(0)


}



</div>



<input


type="file"


accept="image/*"


hidden


onChange={handleProfileImage}


/>


</label>


</div>





<p

style={{

textAlign:"center",

fontSize:13,

color:T.gold,

cursor:"pointer"

}}

>

Click image to upload


</p>







<div

style={{

display:"grid",

gap:18

}}

>



<ProfileInput


label="First Name"


value={
profileForm.first_name
}


onChange={(e)=>

setProfileForm({

...profileForm,

first_name:e.target.value

})

}


/>



<ProfileInput


label="Last Name"


value={
profileForm.last_name
}


onChange={(e)=>

setProfileForm({

...profileForm,

last_name:e.target.value

})

}


/>



<ProfileInput


label="Email"


value={
profileForm.email
}


onChange={(e)=>

setProfileForm({

...profileForm,

email:e.target.value

})

}


/>



</div>







<div

style={{

display:"flex",

justifyContent:"flex-end",

gap:12,

marginTop:30

}}

>


<button

onClick={()=>setShowEditProfile(false)}

className="profile-cta secondary"

>

Cancel

</button>




<button


onClick={saveProfile}



disabled={savingProfile}



className="profile-cta"

style={{

opacity:savingProfile ? .7 : 1

}}



>

{

savingProfile

?

"Saving..."

:

"Save Changes"

}



</button>


</div>





</motion.div>


</motion.div>


)

}
function ProfileInput({

label,

value,

onChange

}){


return(

<div>


<label

style={{

fontSize:12,

color:T.muted,

display:"block",

marginBottom:8

}}

>

{label}

</label>



<input

value={value}

onChange={onChange}


style={{

width:"100%",

padding:"14px 16px",

borderRadius:14,

border:

`1px solid ${T.border}`,

outline:"none",

fontSize:15

}}


/>



</div>

)

}
function ProfileOverview({

user,

setShowEditProfile

}){


return(

<motion.div

initial={{opacity:0,y:15}}

animate={{opacity:1,y:0}}

style={{

background:"#fff",


padding:32,

border:`1px solid ${T.border}`,

boxShadow:"0 15px 40px rgba(0,0,0,.04)"

}}

>



<div

style={{

display:"flex",

justifyContent:"space-between",

alignItems:"center",

marginBottom:30

}}

>


<div>


<p

style={{

fontSize:12,

letterSpacing:".2em",

color:T.gold

}}

>

PERSONAL DETAILS

</p>


<h2

style={{

margin:0

}}

>

Profile Information

</h2>


</div>


</div>





<div

className="profile-grid"

style={{

display:"grid",

gridTemplateColumns:"repeat(2,1fr)",

gap:20

}}

>



<InfoRow

label="First Name"

value={user.first_name}

/>


<InfoRow

label="Last Name"

value={user.last_name}

/>


<InfoRow

label="Email"

value={user.email}

/>


<InfoRow

label="Member Tier"

value="Premium Greenbless Member"

/>


</div>





<button

onClick={()=>setShowEditProfile(true)}

className="profile-cta"

style={{

marginTop:35

}}


>


Edit Profile


</button>



</motion.div>

)

}


function WishlistTab({
    wishlist,
    loading,
    onAddToCart,
    onRemove,
}) {

    wishlist = Array.isArray(wishlist) ? wishlist : [];

    if (loading) {
        return (
            <div
                style={{
                    background: "#fff",
                    borderRadius: 24,
                    padding: 35,
                    border: `1px solid ${T.border}`,
                }}
            >
                Loading wishlist...
            </div>
        );
    }

    if (!wishlist.length) {
        return (
            <EmptyState
                icon="❤"
                title="Your Wishlist is Empty"
                description="Save your favourite products here."
                button={
                    <button
                        className="profile-cta"
                        style={{
                            marginTop: 20,
                        }}
                    >
                        Browse Collection
                    </button>
                }
            />
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
                display: "grid",
                gridTemplateColumns:
                    "repeat(auto-fill,minmax(250px,1fr))",
                gap: 24,
            }}
        >
            {wishlist.map((item) => (

                <motion.div
                    key={item.id}
                    whileHover={{
                        y: -6,
                    }}
                    transition={{
                        duration: .25,
                    }}
                    style={{
                        background: "#fff",
                        
                        overflow: "hidden",
                        border: `1px solid ${T.border}`,
                        boxShadow:
                            "0 12px 35px rgba(0,0,0,.05)",
                    }}
                >

                    <div
                        style={{
                            height: 260,
                            overflow: "hidden",
                            background: T.surface,
                        }}
                    >
                        <img
                            src={item.image}
                            alt={item.name}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: ".4s",
                            }}
                        />
                    </div>

                    <div
                        style={{
                            padding: 20,
                        }}
                    >
                        <h3
                            style={{
                                marginTop: 0,
                                marginBottom: 8,
                                fontSize: 18,
                            }}
                        >
                            {item.name}
                        </h3>

                        <div
                            style={{
                                color: T.gold,
                                fontWeight: 700,
                                fontSize: 18,
                            }}
                        >
                            £ {item.price}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                marginTop: 20,
                            }}
                        >

                            <button
                                className="profile-cta"
                                style={{
                                    flex: 1,
                                }}
                                onClick={() => onAddToCart?.(item)}
                            >
                                Add to Cart
                            </button>

                            <button
                                className="profile-cta-icon"
                                style={{
                                    fontSize: 18,
                                }}
                                onClick={() => onRemove?.(item)}
                            >
                                🗑
                            </button>

                        </div>

                    </div>

                </motion.div>

            ))}
        </motion.div>
    );
}

function AddressesTab({
    addresses,
    loading,
    setShowAddressModal,
    onAddAddress,
    onEditAddress,
    onDeleteAddress,
}) {

    addresses = Array.isArray(addresses) ? addresses : [];

    if (loading) {
        return (
            <div
                style={{
                    background: "#fff",
                    padding: 35,
                    borderRadius: 24,
                    border: `1px solid ${T.border}`,
                }}
            >
                Loading addresses...
            </div>
        );
    }

    if (!addresses.length) {
        return (
            <EmptyState
                icon="📍"
                title="No Saved Addresses"
                description="You haven't added any saved addresses yet."
                button={
                  <button
    onClick={() => (onAddAddress ? onAddAddress() : setShowAddressModal(true))}
    className="profile-cta"
    style={{
        marginTop:20,
    }}
>
    + Add Address
</button>
                }
            />
        );
    }

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 20,
                }}
            >
                <button
                    onClick={() => (onAddAddress ? onAddAddress() : setShowAddressModal(true))}
                    className="profile-cta"
                >
                    + Add Address
                </button>
            </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
                display: "grid",
                gridTemplateColumns:
                    "repeat(auto-fit,minmax(320px,1fr))",
                gap: 24,
            }}
        >
            {addresses.map((address) => (

                <motion.div
                    key={address.id}
                    whileHover={{ y: -5 }}
                    transition={{ duration: .25 }}
                    style={{
                        background: "#fff",
                        
                        border: `1px solid ${T.border}`,
                        padding: 28,
                        boxShadow:
                            "0 12px 35px rgba(0,0,0,.05)",
                        position: "relative",
                    }}
                >

                    {(address.default || address.is_default) && (

                        <div
                            style={{
                                position: "absolute",
                                right: 18,
                                top: 18,
                                background: T.gold,
                                color: "#fff",
                                padding: "6px 14px",
                                borderRadius: 30,
                                fontSize: 12,
                                fontWeight: 700,
                            }}
                        >
                            Default
                        </div>

                    )}

                    <h3
                        style={{
                            marginTop: 0,
                            marginBottom: 12,
                        }}
                    >
                        {address.label || "Address"}
                    </h3>

                    <p
                        style={{
                            color: T.muted,
                            lineHeight: 1.8,
                            marginBottom: 25,
                        }}
                    >
                        {address.address_line1}
                        <br />

                        {address.address_line2 &&
                            <>
                                {address.address_line2}
                                <br />
                            </>
                        }

                        {address.city}

                        {address.state_province &&
                            `, ${address.state_province}`}

                        <br />

                        {address.country}
                    </p>

                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                        }}
                    >

                        <button
                            onClick={() => onEditAddress && onEditAddress(address)}
                            className="profile-cta"
                            style={{
                                flex: 1,
                            }}
                        >
                            Edit
                        </button>

                        <button
                            onClick={() => onDeleteAddress && onDeleteAddress(address)}
                            className="profile-cta secondary"
                            style={{
                                flex: 1,
                            }}
                        >
                            Delete
                        </button>

                    </div>

                </motion.div>

            ))}
        </motion.div>
        </div>
    );
}
const inputStyle = {
  width: "100%",
  padding: "14px 16px",
  border: `1px solid ${T.border}`,
  borderRadius: 14,
  outline: "none",
  fontSize: 15,
  background: "#fff",
  transition: ".25s",
};

const labelStyle = {
  display: "block",
  marginBottom: 8,
  fontSize: 12,
  fontWeight: 700,
  color: T.ink,
  letterSpacing: ".08em",
  textTransform: "uppercase",
};

function AddressModal({
    addressForm,
    setAddressForm,
    setShowAddressModal,
    editingAddress,
    savingAddress,
    onSubmit,
}) {

    return (

        <motion.div
            initial={{opacity:0}}
            animate={{opacity:1}}
            style={{
                position:"fixed",
                inset:0,
                background:"rgba(0,0,0,.45)",
                backdropFilter:"blur(6px)",
                display:"flex",
                justifyContent:"center",
                alignItems:"center",
                zIndex:9999,
            }}
        >

            <motion.div
                initial={{scale:.95,y:20}}
                animate={{scale:1,y:0}}
                style={{
    width: "95%",
    maxWidth: 620,
    maxHeight: "90vh",
    overflowY: "auto",

    background: "#fff",
    
    padding: 35,
    boxShadow: "0 12px 32px rgba(0,0,0,0.05)",
}}
            >

                <h2>{editingAddress ? "Edit Address" : "Add New Address"}</h2>

 <form
    onSubmit={(e)=>{
        e.preventDefault();

        onSubmit && onSubmit();
    }}
    style={{
        marginTop:30,
        display:"grid",
        gap:22,
    }}
>

    <div>
        <label style={labelStyle}>
            Label (e.g. Home, Work)
        </label>

        <input
            className="address-input"
            value={addressForm.label}
            placeholder="Home"
            onChange={(e)=>
                setAddressForm({
                    ...addressForm,
                    label:e.target.value
                })
              
            }
            required
        />
    </div>


    <div
        style={{
            display:"grid",
            gridTemplateColumns:"1fr 1fr",
            gap:18,
        }}
    >

        <div>
            <label style={labelStyle}>
                First Name
            </label>

            <input
                className="address-input"
                value={addressForm.first_name}
                onChange={(e)=>
                    setAddressForm({
                        ...addressForm,
                        first_name:e.target.value
                    })
                }
                required 
            />
        </div>

        <div>
            <label style={labelStyle}>
                Last Name
            </label>

            <input
                className="address-input"

                value={addressForm.last_name}
                onChange={(e)=>
                    setAddressForm({
                        ...addressForm,
                        last_name:e.target.value
                    })
                }
                required
            />
        </div>

    </div>


    <div>
        <label style={labelStyle}>
            Address Line 1
        </label>

        <input
            className="address-input"
            value={addressForm.address_line1}
            onChange={(e)=>
                setAddressForm({
                    ...addressForm,
                    address_line1:e.target.value
                })
            }
            required 
        />
    </div>


    <div>
        <label style={labelStyle}>
            Address Line 2
        </label>

        <input
            className="address-input"
            value={addressForm.address_line2}
            onChange={(e)=>
                setAddressForm({
                    ...addressForm,
                    address_line2:e.target.value
                })
            }
             
        />
    </div>


    <div
        style={{
            display:"grid",
            gridTemplateColumns:"1fr 1fr",
            gap:18,
        }}
    >

        <div>
            <label style={labelStyle}>
                Country
            </label>

            <input
                className="address-input"
                value={addressForm.country}
                onChange={(e)=>
                    setAddressForm({
                        ...addressForm,
                        country:e.target.value
                    })
                }
                required 
            />
        </div>

        <div>
            <label style={labelStyle}>
                State / Province
            </label>

            <input
               className="address-input"
                value={addressForm.state_province}
                onChange={(e)=>
                    setAddressForm({
                        ...addressForm,
                        state_province:e.target.value
                    })
                }
                required 
            />
        </div>

    </div>


    <div
        style={{
            display:"grid",
            gridTemplateColumns:"1fr 1fr",
            gap:18,
        }}
    >

        <div>
            <label style={labelStyle}>
                City
            </label>

            <input
                className="address-input"
                value={addressForm.city}
                onChange={(e)=>
                    setAddressForm({
                        ...addressForm,
                        city:e.target.value
                    })
                }
                required 
            />
        </div>

        <div>
            <label style={labelStyle}>
                Postal Code
            </label>

            <input
                className="address-input"
                value={addressForm.postalcode}
                onChange={(e)=>
                    setAddressForm({
                        ...addressForm,
                        postalcode:e.target.value
                    })
                }
                required
            />
        </div>

    </div>


    <div
        style={{
            display:"flex",
            alignItems:"center",
            gap:12,
            padding:"16px 18px",
            border:`1px solid ${T.border}`,
            borderRadius:14,
            background:T.surface,
        }}
    >

        <input
            type="checkbox"
            checked={addressForm.is_default}
            onChange={(e)=>
                setAddressForm({
                    ...addressForm,
                    is_default:e.target.checked
                })
            }
            style={{
                width:18,
                height:18,
                accentColor:T.gold,
                cursor:"pointer",
            }}
        />

        <span
            style={{
                fontWeight:600,
                color:T.ink,
            }}
        >
            Set as default address
        </span>

    </div>


    <div
        style={{
            display:"flex",
            justifyContent:"flex-end",
            gap:14,
            marginTop:8,
        }}
    >

        <button
            onClick={()=>setShowAddressModal(false)}
            type="button"
            disabled={savingAddress}
            className="profile-cta secondary"
            style={{
                opacity:savingAddress ? .6 : 1,
            }}
        >
            Cancel
        </button>

        <button
            type="submit"
            disabled={savingAddress}
            className="profile-cta"
            style={{
                opacity:savingAddress ? .7 : 1,
            }}
        >
            {savingAddress
                ? "Saving..."
                : editingAddress
                    ? "Update Address"
                    : "Save Address"}
        </button>

    </div>

</form>


            </motion.div>

        </motion.div>

    );
}

function InfoRow({

label,

value

}){


return(

<div

style={{

padding:20,

borderRadius:16,

background:T.surface,

border:`1px solid ${T.border}`

}}

>


<p

style={{

margin:0,

fontSize:12,

color:T.muted,

letterSpacing:".08em",

textTransform:"uppercase"

}}

>

{label}

</p>


<h4

style={{

margin:"8px 0 0",

fontSize:16

}}

>

{value || "-"}

</h4>


</div>

)

}
function EmptyState({

icon,

title,

description,

button

}){


return(

<div

style={{

padding:"60px 20px",

textAlign:"center",

border:`1px solid ${T.border}`,

borderRadius:20

}}

>


<div

style={{

fontSize:45,

marginBottom:15

}}

>

{icon}

</div>


<h3>

{title}

</h3>


<p

style={{

color:T.muted

}}

>

{description}

</p>


{button}


</div>

)

}
